import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import type { Addon } from 'src/apiClients/queries';
import { EntityState } from 'src/apiClients/queries';
import { DataTable } from 'src/components/data-table/DataTable';
import {
    SortDirection,
    toCellString,
    type DataTableCellValue,
    type DataTableColDef,
    type DataTableQueryState,
    type DataTableRow,
} from 'src/components/data-table/DataTable.types';
import { usePageFilter } from 'src/contexts/page-filter-context';
import { defaultFiltersFor } from 'src/filters/entity-filter-registry';
import { i18n as routesI18n } from 'src/hooks/use-imago-mp-routes.i18n';
import {
    ADDON_PRODUCT_NAMES,
    createNaFallback,
    createStateLabel,
} from 'src/utils/table-formatters';

import { useInstance } from '../../InstanceContext';
import { SectionHeading } from '../SectionHeading';
import { CreateAddonModal } from './create-addon-modal/CreateAddonModal';
import { EditAddonModal } from './edit-addon-modal/EditAddonModal';
import { i18n } from './SubscriptionsSection.i18n';
import { useStyles } from './SubscriptionsSection.styles';

const AUTOMATION_ID = 'instance-subscriptions-section';
const INITIAL_QUERY_STATE: DataTableQueryState = {
    sorts: [{ field: 'name', direction: SortDirection.ASC }],
    filters: defaultFiltersFor('addon'),
    search: '',
};

const mapRow = (addon: Addon): DataTableRow => ({
    id: addon.id,
    name: ADDON_PRODUCT_NAMES[addon.name] ?? addon.name,
    productCode: addon.name,
    quoteId: addon.salesforceQuoteNumber,
    serialNumber: addon.id,
    type: addon.type,
    quantity: addon.quantity,
    startDate: addon.startDate,
    finishDate: addon.finishDate,
    state: addon.state,
});

export const SubscriptionsSection = () => {
    const intl = useIntl();
    const { classes } = useStyles();
    const { subscription, highlightId } = useInstance();
    const [queryState, setQueryState] = useState<DataTableQueryState>(INITIAL_QUERY_STATE);
    const { register, unregister } = usePageFilter();
    const [selectedAddon, setSelectedAddon] = useState<Addon | null>(null);
    const [isCreateOpen, setIsCreateOpen] = useState(false);

    const handleRowClick = (row: DataTableRow) => {
        const addon = subscription.addons.find((a) => a.id === row.id);
        if (addon) setSelectedAddon(addon);
    };

    const rows = useMemo<DataTableRow[]>(
        () => subscription.addons.map(mapRow),
        [subscription.addons],
    );

    const columns = useMemo<DataTableColDef[]>(() => {
        const naFallback = createNaFallback(intl);
        const stateLabel = createStateLabel(intl);
        const productNameOptions = Object.values(ADDON_PRODUCT_NAMES).map((name) => ({
            label: name,
            value: name,
        }));
        const productCodeOptions = Object.keys(ADDON_PRODUCT_NAMES).map((code) => ({
            label: code,
            value: code,
        }));
        const dateFormatter = (value: DataTableCellValue): string => {
            if (value === null || value === undefined || value === '') {
                return naFallback(value);
            }
            const date = new Date(String(value));
            if (isNaN(date.getTime())) return naFallback(null);
            return intl.formatDate(date, { year: 'numeric', month: 'short', day: 'numeric' });
        };
        return [
            {
                field: 'name',
                headerName: intl.formatMessage(i18n.name),
                flex: 2,
                minWidth: 160,
                filterOptions: productNameOptions,
                valueFormatter: naFallback,
            },
            {
                field: 'productCode',
                headerName: intl.formatMessage(i18n.productCode),
                width: 200,
                filterOptions: productCodeOptions,
                valueFormatter: naFallback,
            },
            {
                field: 'type',
                headerName: intl.formatMessage(i18n.type),
                width: 90,
                valueFormatter: naFallback,
            },
            {
                field: 'quoteId',
                headerName: intl.formatMessage(i18n.quoteId),
                width: 95,
                valueFormatter: naFallback,
            },
            {
                field: 'quantity',
                headerName: intl.formatMessage(i18n.quantity),
                width: 80,
                valueFormatter: (value) =>
                    value === null || value === undefined ? naFallback(value) : String(value),
            },
            {
                field: 'startDate',
                headerName: intl.formatMessage(i18n.startDate),
                width: 115,
                valueFormatter: dateFormatter,
            },
            {
                field: 'finishDate',
                headerName: intl.formatMessage(i18n.finishDate),
                width: 115,
                valueFormatter: dateFormatter,
            },
            {
                field: 'serialNumber',
                headerName: intl.formatMessage(i18n.serialNumber),
                flex: 2,
                minWidth: 200,
            },
            {
                field: 'state',
                headerName: intl.formatMessage(i18n.status),
                width: 110,
                filterOptions: [
                    { label: stateLabel(EntityState.ACTIVE), value: EntityState.ACTIVE },
                    { label: stateLabel(EntityState.INACTIVE), value: EntityState.INACTIVE },
                    { label: stateLabel(EntityState.ARCHIVED), value: EntityState.ARCHIVED },
                ],
                valueFormatter: stateLabel,
            },
        ];
    }, [intl]);

    const filteredRows = useMemo<DataTableRow[]>(() => {
        const filterEntries = Object.entries(queryState.filters);
        if (filterEntries.length === 0) return rows;
        const normalize = (v: DataTableCellValue): string =>
            v === null || v === undefined ? '' : String(v);
        const normalizedFilters: [string, Set<string>][] = filterEntries.map(([field, vals]) => [
            field,
            new Set(vals.map(normalize)),
        ]);
        return rows.filter((row) =>
            normalizedFilters.every(
                ([field, vals]) => vals.size > 0 && vals.has(normalize(row[field])),
            ),
        );
    }, [rows, queryState.filters]);

    const sortedRows = useMemo<DataTableRow[]>(() => {
        if (queryState.sorts.length === 0) return filteredRows;
        return [...filteredRows].sort((a, b) => {
            for (const { field, direction } of queryState.sorts) {
                const cmp = toCellString(a[field]).localeCompare(
                    toCellString(b[field]),
                    undefined,
                    {
                        numeric: true,
                        sensitivity: 'base',
                    },
                );
                if (cmp !== 0) return direction === SortDirection.ASC ? cmp : -cmp;
            }
            return 0;
        });
    }, [filteredRows, queryState.sorts]);

    const handlePageFilterChange = useCallback((field: string, values: DataTableCellValue[]) => {
        setQueryState((prev) => ({ ...prev, filters: { ...prev.filters, [field]: values } }));
    }, []);

    const filterableColumns = useMemo(
        () =>
            columns
                .filter((c) => c.filterOptions && c.filterOptions.length > 0)
                .map((c) => ({
                    field: c.field,
                    headerName: c.headerName,
                    filterOptions: c.filterOptions!,
                })),
        [columns],
    );

    useEffect(() => {
        if (filterableColumns.length === 0 || rows.length === 0) return;
        register({
            id: AUTOMATION_ID,
            label: intl.formatMessage(routesI18n.instanceSectionSubscriptions),
            columns: filterableColumns,
            filters: queryState.filters,
            onFilterChange: handlePageFilterChange,
        });
    }, [
        register,
        intl,
        filterableColumns,
        queryState.filters,
        handlePageFilterChange,
        rows.length,
    ]);

    useEffect(() => {
        return () => unregister(AUTOMATION_ID);
    }, [unregister]);

    const heading = (
        <div className={classes.headerRow}>
            <SectionHeading
                title={intl.formatMessage(routesI18n.instanceSectionSubscriptions)}
                count={rows.length}
                automationId="instance-subscriptions-count"
            />
            <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setIsCreateOpen(true)}
                automation-id="new-subscription-button"
            >
                {intl.formatMessage(i18n.newSubscription)}
            </Button>
        </div>
    );

    return (
        <>
            {heading}
            <DataTable
                columns={columns}
                rows={sortedRows}
                totalRows={sortedRows.length}
                autoHeight
                queryState={queryState}
                onQueryStateChange={setQueryState}
                titleWhenNoResults={intl.formatMessage(i18n.noSubscriptionsTitle)}
                messageWhenNoResults={intl.formatMessage(i18n.noSubscriptionsMessage)}
                highlightedRowId={highlightId}
                onRowClick={handleRowClick}
            />
            {selectedAddon && (
                <EditAddonModal
                    addon={selectedAddon}
                    subscriptionId={subscription.id}
                    open={selectedAddon !== null}
                    onClose={() => setSelectedAddon(null)}
                />
            )}
            <CreateAddonModal
                subscriptionId={subscription.id}
                open={isCreateOpen}
                onClose={() => setIsCreateOpen(false)}
            />
        </>
    );
};
